import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import AgentsBase from './agents';
import AgentService from '../../../services/agentService';
import { validateAgent } from '../../../validation/agentValidation';
import AgentErrorBoundary from '../../../components/agent/ErrorBoundary';
import {
  setAdminInput,
  getAdminDataFromList,
  resetAdminInput,
  toggleAdminEdit,
  adminActionAlert,
  adminActionStatus,
  changeAdminView,
  setAdminListFilter
} from '../../../redux/actions/admin';
import {
  getAdminListSelector,
  agentListSelector,
  groupAgentsSelector
} from '../../../redux/selectors/admin';
import {
  fetchAdminList,
  fetchAgentData,
  setAgentData,
  fetchAgentGroups,
  saveAgentGroups,
  doActivateAgent,
  doDeactivateAgent,
  doUnlockAgent,
  doGetTwoFAGetNewSecret,
  doVerifyTwoFAToken,
  removeAdminData,
  agentUploadListAttachment,
  agentCreateNew
} from '../../../redux/actions/async/admin';
import { onlyActiveAreasSelector } from '../../../redux/selectors/workflow';
import {
	allowMultipleChatbotProvidersMemo,
	providersMemo
} from '../../../redux/selectors/chatbot';
import { timeZoneListSelector } from '../../../redux/selectors/statistics';
import { M_AGENTS, emptyArray } from '../../../common/v5/constants';
import { I } from '../../../common/v5/config';

// Import access key actions and selectors
import {
  list as listAdminAgentAccessKeys,
  create as createAdminAgentAccessKey,
  deleteKey as deleteAdminAgentAccessKey
} from '../../../redux/actions/async/agentAccessKey';
import { adminAgentAccessKeysSelector } from '../../../redux/selectors/agentAccessKey';
import { adminAgentAccessKeyURL } from '../../../redux/actions/async/ajax';

// Import skills actions and selectors
import {
  saveAgentSkill,
  fetchSkillProficiency,
  fetchSkills,
  removeSkillAgent
} from '../../../redux/actions/async/admin';
import {
  skillProficiencySelector,
  skillsAdminSelector
} from '../../../redux/selectors/admin';
import { updateAgentSkill } from '../../../redux/actions/admin';
import { getWorkflowSettingsData } from '../../../redux/selectors/workflow';
import { isSysAdmin } from '../../../common/v5/helpers';
import { onceAgentAreas, onceConnectedAgentAreas } from '../../../redux/actions/async/workflow';

const FORM_EDIT = 'edit';
const FORM_UPLOAD = 'upload';

const mapStateToProps = (state, props) => {
  const server = state.server;
  const app = state.app;
  const admin = app.admin;
  const ui = admin.ui;
  const adminSt = admin.admin;
  const agentData = admin.agentData;
  const agentNew = admin.agentNew;

  // When creating an agent the option lists (admin levels, interface languages,
  // etc.) come from the AgentNew endpoint stored in the `agentNew` reducer, not
  // from `agentData` which is only populated when editing an existing agent via
  // fetchAgentData. Existing agents have activeId > 0; a new agent leaves it at
  // 0/-1/"".
  const isNewAgent = !(adminSt.activeId > 0);
  const optionSrc = (isNewAgent && agentNew?.data) ? agentNew.data : agentData.data;
  
  // Get access key data for the current agent
  const accessKeyData = adminAgentAccessKeysSelector(state, props);

  
  // Get skills data
  const availableSkills = skillsAdminSelector(state);
  const proficiencyList = skillProficiencySelector(state);

  // Get current user data for sysadmin checks
  const workflowSettings = getWorkflowSettingsData(state);
  const isSysadmin = isSysAdmin(workflowSettings);
  
  return {
    view: ui.view,
    list: agentListSelector(state),
    input: {
      ...adminSt.input,
      avatarPreview: (() => {
        const avatar = adminSt.avatarPreview;
        if (!avatar || avatar === '' || avatar === 'undefined') {
          return null;
        }
        // Ensure it's a string
        return String(avatar);
      })(),
      // Get the active status from the agent list if we're editing an existing agent
      ...(adminSt.activeId > 0 && {
        active: (() => {
          const agentList = agentListSelector(state);
          const currentAgent = agentList.find(agent => agent.id === adminSt.activeId);
          return currentAgent ? currentAgent.status : undefined;
        })()
      })
    },
    showInline: adminSt.showInline,
    activeId: adminSt.activeId,
    formType: FORM_EDIT,
    adminStatus: adminSt.adminStatus,
    areaList: onlyActiveAreasSelector(state),
    availableAreas: agentData.data?.availableAreas || emptyArray,
    langSrc: adminSt.langSrc || optionSrc?.langSrc || emptyArray,
    interfaceLangSrc: adminSt.interfaceLangSrc || optionSrc?.interfaceLangSrc || emptyArray,
    timeZone: optionSrc?.timeZone || adminSt.timeZone || timeZoneListSelector(state, props) || emptyArray,
    salutationList: adminSt.salutationList || emptyArray,
    signatureList: adminSt.signatureList || emptyArray,
    // Add API data for options that were previously hardcoded
    adminLevelSrc: optionSrc?.adminLevelSrc || emptyArray,
    // Group-related props.
    // A new (unsaved) agent has no id, so the aid-keyed GroupAgents fetch
    // (fetchAgentGroups/onGetAgentGroups) never runs for it — it has nothing
    // to key on. The AgentNew endpoint (already fetched via agentCreateNew()
    // in onLoadToEdit below, mirroring the legacy admin flow) returns the
    // full groups list as optionSrc.availableGroups regardless of agent id,
    // so use that instead when there's no saved agent to fetch memberships for.
    groupsAgentList: isNewAgent
      ? { available: optionSrc?.availableGroups || emptyArray, members: emptyArray, teamLeaders: emptyArray }
      : groupAgentsSelector(state),
    groupAgentListReady: isNewAgent ? true : (adminSt.groupAgentListReady || false),
    // Pass the current admin status for monitoring save completion
    currentAdminStatus: adminSt.adminStatus,
    // Access key props
    accessKeyData: accessKeyData,
    // Access key URL creator function
    accessKeyURLCreator: adminAgentAccessKeyURL,

    availableSkills: availableSkills,
    proficiencyList: proficiencyList,
    // Pass sysadmin status to component
    isSysadmin: isSysadmin,
    chatbotProviders: providersMemo(state),
    multipleChatbotProviders: allowMultipleChatbotProvidersMemo(state),
    features: server.features || {},
    previewFeature: !!(
      server.features?.['preview.inbox'] ||
      server.features?.['preview.my-errands'] ||
      server.features?.['preview.search'] ||
      server.features?.['preview.answer']
    ),
    previewSummaryFeature: !!server.features?.['preview-summary']
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const view = M_AGENTS;
  
  return {
    onLoad: () => {
      // Clear any existing search results when loading
      dispatch({
        type: 'AGENT_SEARCH_RESULTS',
        payload: null
      });
      dispatch(changeAdminView(view));
      // Load with default filter (0 = Active agents only, same as old implementation)
      const defaultFilter = { filter: 0 };
      dispatch(fetchAdminList(view, defaultFilter));
    },
    
    onLoadToEdit: (id) => {
      if (id > 0) {
        dispatch(resetAdminInput());
        // Reset avatar preview before loading new agent data
        dispatch(setAdminInput('avatarPreview', ''));
        dispatch(onceAgentAreas());
        dispatch(onceConnectedAgentAreas());
        dispatch(fetchAgentData(id + '/false'));
      } else {
        // Reset avatar preview for new agent
        dispatch(setAdminInput('avatarPreview', ''));
        // Fetch the new-agent template so option lists (admin levels, interface
        // languages, etc.) are populated, mirroring the legacy admin flow.
        dispatch(agentCreateNew());
        const defaultAgent = AgentService.createDefaultAgent();
        dispatch(resetAdminInput());

        let fieldsSet = 0;
        Object.keys(defaultAgent).forEach(key => {
          const value = defaultAgent[key];
          // Set all values including false and 0 for preferences
          if (value !== '' && value !== null && value !== undefined) {
            dispatch(setAdminInput(key, value));
            fieldsSet++;
          }
        });
      }
      dispatch(toggleAdminEdit(id, true, false, true));
    },
    
    onLoadToUpload: () => {
      dispatch(toggleAdminEdit(0, true, false, false));
    },
    
    onChangeAdminInput: (field, value) => {
      dispatch(setAdminInput(field, value));
    },
    
    onSaveAdmin: (inputData) => {

      // NOTE: setAdminData(view) is a no-op for the M_AGENTS view (it has no
      // matching branch and silently falls through to a dev-only console.log),
      // so saving here must go through setAgentData, which is the actual save
      // action used by the agent edit form. It reads straight from
      // admin.admin.input (kept in sync via onChangeAdminInput on every field
      // change) and applies the full V5-to-legacy field mapping itself via
      // mapV5FieldsToApiFormat, so there's no need to duplicate that mapping
      // here.
      dispatch(adminActionStatus({ status: 1, msg: I('Saving agent...') }));

      if (inputData && inputData.active !== undefined) {
        dispatch(setAdminInput('active', inputData.active));
      }

      dispatch(setAgentData(false));

    },
    
    onDeleteFromList: (id) => {
      // Handle agent removal using the same logic as other admin views
      dispatch(removeAdminData({id: id}, view));
    },
    
    onCloseForm: () => {
      // Reset avatar preview before closing form
      dispatch(setAdminInput('avatarPreview', ''));
      dispatch(toggleAdminEdit(0, false, false, false));
      dispatch(resetAdminInput());
    },
    
    onFetchAdminList: () => {
      // Use default filter (0 = Active agents) when refreshing list
      const defaultFilter = { filter: 0 };
      dispatch(fetchAdminList(view, defaultFilter));
    },
    
    // Filter change handler - fetches agents with filter parameter
    onFilterChange: (filterValue) => {
      // First, update the Redux state with the new filter value
      dispatch(setAdminListFilter('agentTypeFilter', filterValue));
      
      // Then dispatch action to fetch filtered agent list
      const paramObject = { filter: filterValue };
      dispatch(fetchAdminList(view, paramObject));
    },
    
    // Group-related actions
    onGetAgentGroups: (agentId) => {
      dispatch(fetchAgentGroups(agentId));
    },
    
    onSaveAgentGroups: (agentId, memberIds, teamLeaderIds) => {
      dispatch(saveAgentGroups(agentId, memberIds, teamLeaderIds));
    },
    
    // Access key actions
    onCreateAgentAccessKey: (agentID, params) => {
      console.log('=== Creating access key for agent:', agentID, 'with params:', params);
      return dispatch(createAdminAgentAccessKey(agentID, params));
    },
    
    onDeleteAgentAccessKey: (agentID, accessKeyID) => {
      return dispatch(deleteAdminAgentAccessKey(agentID, accessKeyID));
    },
    
    onLoadAgentAccessKeys: (agentID) => {
      console.log('=== Loading access keys for agent:', agentID);
      return dispatch(listAdminAgentAccessKeys(agentID));
    },
    
    // Skills actions
    onLoadSkills: () => {
      dispatch(fetchSkillProficiency());
      dispatch(fetchSkills());
    },
    
    onSaveAgentSkill: (params) => {
      console.log('=== Saving agent skill with params:', params);
      return dispatch(saveAgentSkill(params));
    },
    
    onRemoveAgentSkill: (params) => {
      console.log('=== Removing agent skill with params:', params);
      return dispatch(removeSkillAgent(params));
    },
    
    onUpdateAgentSkill: (skill, isRemove) => {
      console.log('=== Updating agent skill:', skill, 'isRemove:', isRemove);
      return dispatch(updateAgentSkill(skill, isRemove));
    },
    
    // Agent activation/deactivation
    onActivateAgent: (status, id) => {
      console.log('=== Activating/Deactivating agent:', id, 'status:', status);
      if (status) {
        return dispatch(doActivateAgent(id));
      } else {
        return dispatch(doDeactivateAgent(id));
      }
    },

    onUnlockAgent: (id) => {
      return dispatch(doUnlockAgent(id));
    },

    getTwoFANewSecret: () => {
      return dispatch(doGetTwoFAGetNewSecret());
    },

    onVerifyTwoFAToken: (twofa_secret, twofa_token) => {
      return dispatch(doVerifyTwoFAToken({ twofa_secret, twofa_token }));
    },
    
    // Agent search functionality - exactly like the old working system
    onSearchAgents: (searchText, filterValue = 0) => {
      return new Promise((resolve, reject) => {
        $.get('/admin/agents/list', {
          "searchText": searchText || '',
          "filter": filterValue
        })
        .done((data) => {
          resolve(data);
        })
        .fail((error) => {
          console.error('=== Search failed:', error);
          reject(error);
        });
      });
    },
    
    // Agent upload functionality
    onUploadAgents: (files) => {
      console.log('=== Uploading agents with files:', files);
      dispatch(agentUploadListAttachment(files));
    }
  };
};

const withAgents = compose(
  connect(mapStateToProps, mapDispatchToProps)
);

// Wrap with error boundary for better error handling
const AgentsCtnr = (props) => (
  <AgentErrorBoundary context="agent-list">
    {React.createElement(withAgents(AgentsBase), props)}
  </AgentErrorBoundary>
);

export default AgentsCtnr; 